/**
 * `AW46` · Borde HTTP del **catálogo de plantillas por brazo**.
 *
 * Es el ÚNICO archivo de `plantillas/` que emite `fetch`, igual que
 * `borde-exportacion.ts` lo es de `exportacion/`. La pantalla consume esto y no
 * sabe de rutas ni de códigos de estado.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ① ESTA UNIDAD NO LE HABLA A META, Y NO PUEDE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `AW46` §`guard` ②. Los dos pedidos van contra `apps/admin-web`
 * (`config.adminWebUrl`), que es lo mismo que hacen los otros seis bordes de esta
 * aplicación. **Pegarle a la Graph API desde acá exigiría que la credencial WABA
 * del brazo viajara al navegador de quien abre la pantalla** —`management-ui` es
 * un artefacto estático en S3 + CloudFront, «un bucket, no un proceso»— y la
 * cuenta es del cliente.
 *
 * ⭐ **Y el disparo manual recorre EL MISMO camino que el sondeo programado**:
 * `POST brazos/{brazo}/plantillas/sincronizaciones` proyecta
 * `TemplatesAdminApi.requestSync(arm)`, que delega en el mismo puerto inyectado
 * que usa la tarea con reloj. No hay un segundo camino hacia la lista — que es el
 * modo de falla que `CC4` guard 2 nombra textual: *«el botón manual y la tarea
 * programada terminan siendo dos caminos distintos hacia la misma lista»*.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ② DEL REPO ENTRAN SÓLO TIPOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Misma regla que los otros bordes: los imports de `contracts/**` son
 * `import type`. El alias `@contracts/*` vive en `tsconfig.json` y **NO** en
 * `vite.config.ts`, a propósito, así que importar un VALOR de un contrato hace
 * fallar el build. Por eso los tres segmentos de ruta se repiten acá como
 * literales **tipados contra `TemplateCatalogPath` y `TemplateSyncPath`**: el día
 * que una ruta cambie en `contracts/admin-api/http.ts`, este build se cae en vez
 * de seguir hablándole a un endpoint que no existe.
 *
 * ⭐⭐ **Y `ESTADO_APROBADA_EN_META` es la misma disciplina aplicada a un VALOR
 * que no se puede importar** (`ADR-0247`). La anotación
 * `: TemplateApprovedStatus` —que es `typeof TEMPLATE_APPROVED_STATUS` de
 * `contracts/channel`— es lo único que hace que `tsc` sostenga ese `"APPROVED"`.
 * Escrito suelto, un typo o un renombre del literal **no rompen nada**: dejan el
 * selector VACÍO en silencio, que es la dirección de falla que `ADR-0008` ya
 * eligió al revés. Con la anotación, cualquier otra cadena es
 * `error TS2322: Type '"…"' is not assignable to type '"APPROVED"'`.
 *
 * ⛔ **Y no es el tipo de `approvalStatus`.** `TemplateRecord.approvalStatus` es
 * `string` ABIERTO y se queda abierto: es lo que Meta devolvió, cualquier cosa
 * que sea, y cruza crudo hasta la pantalla para que el operador reconcilie contra
 * el WhatsApp Manager. Esta constante describe **UN valor posible** de ese campo:
 * el único que significa «aprobada» para nosotros.
 */

import type {
  Arm,
  SendListPath,
  SendListTargetPath,
  TemplateCatalogPath,
  TemplateSyncPath,
} from "@contracts/admin-api/http.ts"
import type {
  DispatchTemplateRow,
  TemplateApprovedStatus,
  TemplateButton,
  TemplateCatalogSnapshot,
  TemplateRecord,
  TemplateSendability,
  TemplateSyncAttempt,
} from "@contracts/admin-api/index.ts"
import type { ConfiguracionInstancia } from "../configuracion.ts"
import { mensajeDeError } from "../mensaje-de-error.ts"

/**
 * ⭐⭐ El literal de Meta que decide NUESTRA pregunta, sostenido por `tsc`.
 *
 * Ver el bloque ② del encabezado. Se declara acá —en el borde— y no en cada
 * pantalla, para que haya **una sola escritura** del literal en toda la
 * aplicación: dos copias divergen sin que nada se ponga rojo.
 */
export const ESTADO_APROBADA_EN_META: TemplateApprovedStatus = "APPROVED"

/**
 * Un botón de una plantilla, tal como la sincronización lo trajo.
 *
 * **Se re-exporta el tipo del contrato; no se redeclara.** Una forma «de la
 * pantalla» —un `etiquetaLegible`, un `posicion: number` agregado acá— sería un
 * campo derivado que después nadie puede reconciliar contra el WhatsApp Manager.
 */
export type BotonDePlantilla = TemplateButton

/** Una plantilla del catálogo de un brazo. Mismo criterio que `BotonDePlantilla`. */
export type PlantillaDelBrazo = TemplateRecord

/** El catálogo de un brazo en un instante: las plantillas **y** el instante. */
export type CatalogoDelBrazo = TemplateCatalogSnapshot

/**
 * La fuente de plantillas que consume un SELECTOR, con el brazo ya resuelto.
 *
 * ⭐ **Devuelve las plantillas CRUDAS, sin filtrar por estado**, y eso es
 * deliberado: quien filtra es cada selector, mirando `approvalStatus` contra
 * `ESTADO_APROBADA_EN_META`. Un `cargarAprobadas()` escondería la decisión en un
 * helper y dejaría a los dos sitios sin nada que mirar — que es justamente lo que
 * `AW46` §`goal` pide que se vea en el sitio.
 */
export type CargarPlantillas = () => Promise<readonly PlantillaDelBrazo[]>

/**
 * La fuente vacía por defecto.
 *
 * Mismo patrón que `SIN_FUENTE_DE_AUDIENCIA` de `envios/pantalla.tsx`: sin brazo
 * declarado en la ruta **no hay catálogo que pedir**, y la respuesta honesta es
 * ninguna plantilla, no una lista inventada. Ver `App.tsx` §`useCargarPlantillas`
 * y la costura abierta (c) de `AW46`.
 */
export const SIN_CATALOGO_DE_PLANTILLAS: CargarPlantillas = () => Promise.resolve([])

/**
 * ⭐⭐ `R2-UI1` · **El último INTENTO de sincronización del brazo.**
 *
 * Se re-exporta el tipo del contrato (`TemplateSyncAttempt`); no se redeclara,
 * por lo mismo que `BotonDePlantilla`.
 *
 * ⛔ **No es `lastSyncedAt`, y la diferencia es todo el punto de `B14`.** Un
 * intento que FALLÓ no mueve la última sincronización buena, así que un
 * `lastSyncedAt` viejo es compatible con «no se intentó» y con «se intentó y
 * falló». Éste es el hecho que separa los dos, y es lo que la pantalla sondea
 * tras el `202` para saber cuándo dejar de decir «Sincronizando…».
 */
export type IntentoDeSincronizacion = TemplateSyncAttempt

/**
 * ⭐⭐ `R2-UI1` · **Una fila de «Plantillas para envíos», ya cruzada contra el
 * catálogo.**
 *
 * ⛔⛔ **`sendability` ES EL DEL BORDE Y NO SE RECALCULA ACÁ** (`B13`, `RT 9`,
 * `PRD_R2.md:845`). Viene tal cual de `GET brazos/{brazo}/plantillas-para-envios`,
 * que lo saca del mismo catálogo del que sale `canSend` en el motor de envío. La
 * tentación —derivarlo de `catalog.approvalStatus`, que acá está a mano— deja DOS
 * lugares decidiendo: el día que diverjan, la pantalla dice «Sí» y el envío falla
 * con «La plantilla no se puede usar», que es exactamente lo que el PRD prohíbe.
 *
 * ⚠️⚠️ **`catalog` es `null` cuando la plantilla NO está en el catálogo, y ese
 * cruce lo hace ESTE archivo porque el cable no lo trae.** `DispatchTemplateRow`
 * —medido el 23/9/2026 sobre `contracts/channel/index.ts:4831`— tiene
 * `{arm, name, language, sendability, lastSyncedAt}` y **ninguna de las cuatro
 * columnas que `R2-F2-CA-01`…`CA-05`, `CA-10`, `CA-11` y `CA-12` piden**
 * (Categoría, Variables, Estado en Meta, Botones). Ésas viven en `TemplateRecord`,
 * o sea en el catálogo. ⇒ el borde pide las DOS colecciones y las junta por la
 * terna `(name, language)`.
 *
 * ⛔ **El cruce NO puede decidir el veredicto.** `catalog === null` alimenta UNA
 * celda —«Estado en Meta: No vino en la última sincronización»— y nada más. Que
 * además no se pueda enviar lo dice `sendability`, que ya lo trae decidido.
 */
export type FilaParaEnvios = {
  readonly name: string
  readonly language: string
  readonly sendability: TemplateSendability
  /** La plantilla en el catálogo del brazo, o `null` si el catálogo no la tiene. */
  readonly catalog: PlantillaDelBrazo | null
}

/**
 * La lista de un brazo, con los DOS hechos del brazo que la pantalla necesita.
 *
 * `lastSyncedAt` responde «¿de cuándo es lo que sabemos?» (regla 14, `CA-61`) y
 * `lastAttempt` responde «¿cómo salió el último intento?» (`B14`, `CA-46`,
 * `CA-62`). **Son dos y se guardan como dos**, por el mismo motivo por el que
 * `veredicto.ts` tiene dos decisiones y no una.
 */
export type ListaParaEnvios = {
  readonly filas: readonly FilaParaEnvios[]
  readonly lastSyncedAt: string | null
  readonly lastAttempt: IntentoDeSincronizacion
  /**
   * ⭐ El catálogo ENTERO del brazo, que es de donde el panel «Agregar plantilla»
   * saca lo que ofrece (regla 5, `CA-21`…`CA-24`).
   *
   * ⚠️ **Va acá y no en un segundo pedido porque este borde YA lo trajo**: el
   * cruce de `FilaParaEnvios.catalog` lo necesita. Un `catalogo(brazo)` aparte
   * para el panel sería un tercer `GET` a la misma URL, servido por la misma
   * respuesta, con la ventana de tiempo entre los dos como regalo: la lista y el
   * catálogo dejarían de ser del mismo instante, y ahí «ya está en la lista»
   * (`CA-23`) se calcula contra dos fotos distintas.
   *
   * ⛔ **Sin filtrar por estado y sin ordenar**, igual que `catalogo()`: quien
   * filtra por `ESTADO_APROBADA_EN_META` es el panel, a la vista.
   */
  readonly catalogoDelBrazo: readonly PlantillaDelBrazo[]
}

/**
 * El borde del catálogo visto desde el navegador.
 *
 * ⛔⭐ **RASTRO · 23/9/2026, `R2-UI1`. ACÁ DECÍA «DOS operaciones y ninguna
 * más».** Seguían siendo las dos de `TemplatesAdminApi` mientras la pantalla
 * mostraba el CATÁLOGO. `R2-F2` le da a la pantalla una segunda colección —la
 * LISTA «Plantillas para envíos», que es lo que se ofrece en Nuevo envío— y ésa
 * sí se escribe desde acá: entra por «Agregar plantilla» y sale por «Quitar»
 * (`PRD_R2.md:813`). Son las tres de `SendListAdminApi`, ni una más.
 *
 * ⛔ **Lo que sigue sin existir es crear, editar o borrar una PLANTILLA**: se
 * crean en el WhatsApp Manager del brazo (PRD L735) y este producto no las
 * escribe. Agregar a la lista no crea nada en Meta.
 */
export type BordePlantillas = {
  /** `GET brazos/{brazo}/plantillas`. El snapshot del dominio, tal cual. */
  readonly catalogo: (brazo: Arm) => Promise<CatalogoDelBrazo>
  /**
   * `POST brazos/{brazo}/plantillas/sincronizaciones`. **PIDE** una
   * sincronización.
   *
   * ⭐ **Su único argumento es el BRAZO, y no acepta una lista.** Un borde que
   * aceptara una lista de plantillas sería el segundo camino hacia la misma
   * lista, con el agravante de que quien se la manda es el navegador.
   *
   * **`Promise<void>`, y es una afirmación, no una omisión.** Lo que esta
   * operación completa es PEDIR: el `202` no dice que Meta contestó. El único
   * hecho verificable sobre la frescura es `lastSyncedAt`, y se pide por la otra
   * ruta.
   */
  readonly requestSync: (brazo: Arm) => Promise<void>
  /**
   * `GET brazos/{brazo}/plantillas-para-envios` **+** `GET brazos/{brazo}/plantillas`,
   * cruzados por `(name, language)`.
   *
   * ⭐ **DOS pedidos y UNA operación, a propósito.** La pantalla necesita las dos
   * colecciones para dibujar una sola tabla, y dejar el cruce en la pantalla
   * pondría a un `.tsx` a decidir qué fila va con cuál — que es donde se cuela el
   * atajo de derivar el veredicto del `approvalStatus` que quedó a mano. Acá el
   * cruce alimenta una celda y el veredicto llega decidido.
   *
   * ⚠️ **Si el catálogo falla, la operación falla ENTERA.** Un catálogo caído
   * dejaría todas las filas con `catalog: null`, o sea la tabla entera diciendo
   * «No vino en la última sincronización» — un hecho falso, indistinguible del
   * verdadero. Es la mitad de `ADR-0244` que este repo ya pagó una vez: «no llegó»
   * no tiene representación del lado del dato, así que se dice como error.
   */
  readonly listaParaEnvios: (brazo: Arm) => Promise<ListaParaEnvios>
  /**
   * `POST brazos/{brazo}/plantillas-para-envios` con `{ name, language }`. `201`.
   *
   * ⛔ **El `actor` NO viaja**: `contracts/admin-api/http.ts` §`SendListAddRequestBody`
   * lo prohíbe con nombre —lo resuelve `apps/admin-web` desde la sesión—. Un actor
   * en el cuerpo dejaría que el navegador eligiera quién queda en la auditoría.
   */
  readonly agregar: (brazo: Arm, name: string, language: string) => Promise<void>
  /**
   * `DELETE brazos/{brazo}/plantillas-para-envios/{name}/{language}`, **sin
   * cuerpo**. `204`.
   *
   * La terna va en la RUTA porque es lo que identifica la fila, y no hay cuerpo
   * que pueda llevar un segundo criterio.
   */
  readonly quitar: (brazo: Arm, name: string, language: string) => Promise<void>
}

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

/*
 * El acuerdo de cable, todo junto y en un solo lugar. Los valores repiten los
 * literales del contrato; los TIPOS de path son del contrato, así que una
 * divergencia rompe el typecheck y no pasa desapercibida.
 *
 * ⚠️ El `{brazo}` va percent-encoded: `Arm = string` y ningún contrato fija su
 * forma, así que no se puede asumir que sea seguro para una URL.
 */
const RUTA_BRAZOS = "brazos"
const SEGMENTO_PLANTILLAS = "plantillas"
const SEGMENTO_SINCRONIZACIONES = "sincronizaciones"

function rutaDelCatalogo(brazo: Arm): TemplateCatalogPath {
  return `${RUTA_BRAZOS}/${encodeURIComponent(brazo)}/${SEGMENTO_PLANTILLAS}`
}

function rutaDeLaSincronizacion(brazo: Arm): TemplateSyncPath {
  return `${RUTA_BRAZOS}/${encodeURIComponent(brazo)}/${SEGMENTO_PLANTILLAS}/${SEGMENTO_SINCRONIZACIONES}`
}

/*
 * ⛔ **El segmento de la LISTA es uno solo y NO cuelga del catálogo**
 * (`contracts/admin-api/http.ts` §`SEND_LIST_SEGMENT`): un
 * `plantillas/para-envios` se leería como sub-recurso del catálogo, y el catálogo
 * se reemplaza ENTERO en cada sincronización mientras la lista no.
 *
 * ⚠️ El valor se escribe a mano y **lo sostiene el tipo** `SendListPath`, igual
 * que los otros tres: `ADR-0330` prohíbe importar VALORES de `contracts/` —el
 * alias `@contracts/*` no está en `vite.config.ts`, así que `tsc` pasaría y el
 * build se caería—. Un typo acá es `error TS2322`, no un `404` en producción.
 */
const SEGMENTO_LISTA = "plantillas-para-envios"

function rutaDeLaLista(brazo: Arm): SendListPath {
  return `${RUTA_BRAZOS}/${encodeURIComponent(brazo)}/${SEGMENTO_LISTA}`
}

function rutaDeUnaFila(brazo: Arm, name: string, language: string): SendListTargetPath {
  return `${rutaDeLaLista(brazo)}/${encodeURIComponent(name)}/${encodeURIComponent(language)}`
}

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null && !Array.isArray(valor)
}

/**
 * Un botón del cable.
 *
 * ⛔⭐ **RASTRO · 23/9/2026, `R2-UI1`. ACÁ DECÍA «dos campos, los dos `string`,
 * los dos obligatorios».** `R2-CCH1` anuló esa contención en
 * `contracts/channel` (`TemplateButton` §RASTRO): `position` —el orden de Meta,
 * **desde 1**— y `url` —`null` para los tipos que no llevan— entran porque
 * `R2-F2-CA-11`/`CA-12` piden que la columna diga a qué posición apunta cada
 * botón y a dónde lleva. El lector se estrecha con el contrato y no al revés.
 *
 * ⚠️ **`position` NO se rellena con el índice del arreglo.** Si el cable no lo
 * trae, la respuesta no se entiende y se dice: inventarlo con `i + 1` produciría
 * un número plausible y ajeno, sobre el campo que Meta usa para identificar el
 * botón. Es la misma dirección de falla que `lastSyncedAt`.
 */
function leerBoton(valor: unknown): BotonDePlantilla | null {
  if (!esObjeto(valor)) return null

  const type = valor["type"]
  if (typeof type !== "string") return null

  const text = valor["text"]
  if (typeof text !== "string") return null

  const position = valor["position"]
  if (typeof position !== "number") return null

  const urlCruda = valor["url"]
  if (urlCruda !== null && typeof urlCruda !== "string") return null
  const url: string | null = urlCruda

  return { type, text, position, url }
}

/**
 * Una plantilla del cable.
 *
 * ⛔ **`approvalStatus` se acepta como `string`, sin lista blanca.** Validar
 * contra un juego de valores conocidos sería el enum cerrado por la puerta de
 * atrás: el día que Meta devuelva un estado nuevo, la plantilla desaparecería del
 * catálogo sin que nadie lo haya pedido, y es la que el operador está buscando.
 *
 * ⭐ **Los botones conservan el orden del arreglo.** Se recorren con `for…of` y
 * se apilan; no se ordenan, no se agrupan y no se deduplican. La posición ES el
 * índice por el que el envío identifica a qué botón apunta.
 */
function leerPlantilla(valor: unknown): PlantillaDelBrazo | null {
  if (!esObjeto(valor)) return null

  const name = valor["name"]
  if (typeof name !== "string") return null

  const language = valor["language"]
  if (typeof language !== "string") return null

  const approvalStatus = valor["approvalStatus"]
  if (typeof approvalStatus !== "string") return null

  const webhookEventCrudo = valor["webhookEvent"]
  if (webhookEventCrudo !== null && typeof webhookEventCrudo !== "string") return null
  const webhookEvent: string | null = webhookEventCrudo

  const botonesCrudos = valor["buttons"]
  if (!Array.isArray(botonesCrudos)) return null
  const buttons: BotonDePlantilla[] = []
  for (const crudo of botonesCrudos) {
    const boton = leerBoton(crudo)
    if (boton === null) return null
    buttons.push(boton)
  }

  /*
   * ⭐ `R2-CCH1` · **la categoría viaja CRUDA y `null` es un valor, no un hueco.**
   * `contracts/channel` ⟦el-vocabulario-ajeno-viaja-crudo⟧: traducir acá dejaría a
   * un valor nuevo de Meta sin forma de mostrarse «tal cual» (`R2-F2-CA-10` por
   * analogía). El `null` significa «la consulta no la trajo» y lo dice la pantalla
   * («Sin categoría»), no este archivo.
   */
  const categoryCruda = valor["category"]
  if (categoryCruda !== null && typeof categoryCruda !== "string") return null
  const category: string | null = categoryCruda

  /*
   * ⛔ **El ORDEN del arreglo es dato.** `TemplateRecord.variables` está ordenado
   * porque ese orden es el del relleno posicional de Meta: desordenarlo manda los
   * valores cambiados. Se recorre y se apila; no se ordena y no se deduplica.
   */
  const variablesCrudas = valor["variables"]
  if (!Array.isArray(variablesCrudas)) return null
  const variables: string[] = []
  for (const cruda of variablesCrudas) {
    if (typeof cruda !== "string") return null
    variables.push(cruda)
  }

  return { name, language, buttons, approvalStatus, webhookEvent, category, variables }
}

/**
 * El snapshot del cable: `templates` y `lastSyncedAt`, **dos campos y dos
 * hechos**.
 *
 * ⛔⛔ **Un `lastSyncedAt` ausente NO se rellena.** `null` viaja como `null` en el
 * JSON y significa «este brazo nunca se sincronizó»; un campo que llegue ausente
 * o con otro tipo es una respuesta que no se entiende, y se dice, en vez de
 * caer a un `?? new Date()` que le inventaría frescura a un catálogo que no la
 * tiene. Distinguir «nunca se sincronizó» de «vino sin el campo» es la mitad del
 * motivo por el que esta pantalla existe.
 */
function leerCatalogo(valor: unknown): CatalogoDelBrazo | null {
  if (!esObjeto(valor)) return null

  const crudas = valor["templates"]
  if (!Array.isArray(crudas)) return null

  const templates: PlantillaDelBrazo[] = []
  for (const cruda of crudas) {
    const plantilla = leerPlantilla(cruda)
    if (plantilla === null) return null
    templates.push(plantilla)
  }

  if (!("lastSyncedAt" in valor)) return null
  const crudoInstante = valor["lastSyncedAt"]
  if (crudoInstante !== null && typeof crudoInstante !== "string") return null
  const lastSyncedAt: string | null = crudoInstante

  return { templates, lastSyncedAt }
}

/**
 * ⭐ `R2-UI1` · El último intento, que `R2-CAA1` sumó al MISMO cuerpo del catálogo
 * (`TemplateCatalogResponseBody = TemplateCatalogSnapshot & TemplateSyncAttempt`).
 *
 * ⛔⛔ **Un campo ausente NO se rellena con `null` en silencio**, por lo mismo que
 * `lastSyncedAt`: `null` significa «nunca se intentó», y un cuerpo que vino sin el
 * campo es una respuesta que no se entiende. Los dos casos se leen distinto —uno
 * apaga «Sincronizando…» y el otro es un servidor que no cumple el contrato— y
 * colapsarlos deja a la pantalla sondeando para siempre.
 */
function leerIntento(valor: unknown): IntentoDeSincronizacion | null {
  if (!esObjeto(valor)) return null

  if (!("lastAttemptAt" in valor)) return null
  const crudoAt = valor["lastAttemptAt"]
  if (crudoAt !== null && typeof crudoAt !== "string") return null

  if (!("lastAttemptOutcome" in valor)) return null
  const crudoOutcome = valor["lastAttemptOutcome"]
  if (crudoOutcome !== null && typeof crudoOutcome !== "string") return null

  return { lastAttemptAt: crudoAt, lastAttemptOutcome: crudoOutcome }
}

/**
 * Una fila de la lista, tal como `GET …/plantillas-para-envios` la manda.
 *
 * ⛔ **`sendability` se lee estrechándolo a los TRES `kind` del contrato y nada
 * más.** Aceptar un cuarto valor dejaría a la pantalla con un veredicto que su
 * `switch` no cubre, y el `switch` es exhaustivo a propósito: el día que el
 * contrato gane una variante, esto deja de compilar y alguien contesta qué texto
 * le toca, en vez de que la celda quede vacía.
 */
function leerVeredicto(valor: unknown): TemplateSendability | null {
  if (!esObjeto(valor)) return null
  const kind = valor["kind"]
  if (kind === "aprobada" || kind === "no-aprobada" || kind === "no-sincronizada") return { kind }
  return null
}

function leerFilaDeLaLista(valor: unknown): DispatchTemplateRow | null {
  if (!esObjeto(valor)) return null

  const arm = valor["arm"]
  if (typeof arm !== "string") return null

  const name = valor["name"]
  if (typeof name !== "string") return null

  const language = valor["language"]
  if (typeof language !== "string") return null

  const sendability = leerVeredicto(valor["sendability"])
  if (sendability === null) return null

  if (!("lastSyncedAt" in valor)) return null
  const crudoInstante = valor["lastSyncedAt"]
  if (crudoInstante !== null && typeof crudoInstante !== "string") return null

  return { arm, name, language, sendability, lastSyncedAt: crudoInstante }
}

function leerLista(valor: unknown): readonly DispatchTemplateRow[] | null {
  if (!esObjeto(valor)) return null

  const crudas = valor["templates"]
  if (!Array.isArray(crudas)) return null

  const filas: DispatchTemplateRow[] = []
  for (const cruda of crudas) {
    const fila = leerFilaDeLaLista(cruda)
    if (fila === null) return null
    filas.push(fila)
  }
  return filas
}

/**
 * Arma el borde del catálogo contra la `admin-web` configurada.
 *
 * Función y no módulo con estado, por lo mismo que los otros bordes: la dirección
 * llega en tiempo de ejecución (`configuracion.json`) y no hay ningún momento de
 * import en el que conocerla.
 */
export function crearBordePlantillasHttp(config: ConfiguracionInstancia): BordePlantillas {
  const raiz = config.adminWebUrl

  /**
   * `credentials: "include"` en los dos pedidos y ningún `Authorization` armado
   * acá, igual que en los otros bordes: la sesión la maneja `admin-web` en una
   * cookie `HttpOnly` que el navegador envía solo. Un token que el JavaScript
   * tuviera que leer para ponerlo en un header sería un token que la pantalla
   * tuvo que GUARDAR, y esta unidad no guarda nada.
   */
  const pedir = async (ruta: string, init: RequestInit): Promise<Response> => {
    let respuesta: Response
    try {
      respuesta = await fetch(`${raiz}/${ruta}`, { ...init, credentials: "include" })
    } catch (causa) {
      throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${raiz}).`, 0), {
        cause: causa,
      })
    }
    if (!respuesta.ok) {
      throw errorDelBorde(
        await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
        respuesta.status,
      )
    }
    return respuesta
  }

  return {
    /**
     * `GET brazos/{brazo}/plantillas`.
     *
     * **Un brazo del que nunca se trajo nada contesta `200`**, con
     * `templates: []` y `lastSyncedAt: null`. No es un error y no se convierte en
     * uno: distinguir «brazo que no existe» de «brazo del que nunca se
     * sincronizó» exigiría una lista de brazos que ninguna fuente del repo tiene
     * (`Arm = string`, a propósito).
     */
    async catalogo(brazo: Arm): Promise<CatalogoDelBrazo> {
      const ruta: TemplateCatalogPath = rutaDelCatalogo(brazo)

      const respuesta = await pedir(ruta, {
        method: "GET",
        headers: { Accept: "application/json" },
      })

      const catalogo = leerCatalogo(await respuesta.json())
      if (catalogo === null) {
        throw errorDelBorde(
          "El servidor contestó el catálogo de plantillas en una forma que no se entiende.",
          502,
        )
      }
      return catalogo
    },

    /**
     * `POST brazos/{brazo}/plantillas/sincronizaciones`, **SIN CUERPO**.
     *
     * ⛔ **No hay cuerpo, y la ausencia ES la decisión**: un cuerpo es el único
     * lugar por donde una lista de plantillas podría entrar. El único dato que la
     * operación necesita —el brazo— ya está en la ruta.
     *
     * Contesta `202` sin cuerpo: no se lee `.json()`. Lo que se completó es el
     * PEDIDO; si sirvió de algo se ve en `lastSyncedAt`, por la otra ruta.
     */
    async requestSync(brazo: Arm): Promise<void> {
      const ruta: TemplateSyncPath = rutaDeLaSincronizacion(brazo)

      await pedir(ruta, {
        method: "POST",
        headers: { Accept: "application/json" },
      })
    },

    /**
     * La LISTA del brazo, cruzada contra su catálogo. Ver `ListaParaEnvios`.
     *
     * ⭐ **Los dos pedidos salen a la vez (`Promise.all`) y acá eso SÍ corresponde**,
     * al revés que en la pantalla con N brazos: no son dos cosas que se puedan
     * dibujar por separado, son las dos mitades de UNA tabla. Media tabla no es un
     * resultado parcial útil: es una tabla que miente sobre la otra mitad.
     *
     * ⛔ **El orden es el que devolvió el servidor y no se toca.** `PRD_R2.md:813`
     * pide «la agregada más recientemente arriba», y `DispatchTemplateRow` —medido
     * el 23/9/2026— **no tiene `addedAt`**: ordenar acá exigiría inventar un
     * criterio (¿alfabético? ¿al revés?) que se vería igual de plausible y sería
     * otro. Hueco declarado, y es del borde de `R2-AW1`, que es quien tiene la
     * columna.
     */
    async listaParaEnvios(brazo: Arm): Promise<ListaParaEnvios> {
      const rutaLista: SendListPath = rutaDeLaLista(brazo)
      const rutaCatalogo: TemplateCatalogPath = rutaDelCatalogo(brazo)

      const [respuestaLista, respuestaCatalogo] = await Promise.all([
        pedir(rutaLista, { method: "GET", headers: { Accept: "application/json" } }),
        pedir(rutaCatalogo, { method: "GET", headers: { Accept: "application/json" } }),
      ])

      const filasCrudas = leerLista(await respuestaLista.json())
      if (filasCrudas === null) {
        throw errorDelBorde(
          "El servidor contestó la lista de plantillas para envíos en una forma que no se entiende.",
          502,
        )
      }

      const cuerpoDelCatalogo: unknown = await respuestaCatalogo.json()
      const catalogo = leerCatalogo(cuerpoDelCatalogo)
      const intento = leerIntento(cuerpoDelCatalogo)
      if (catalogo === null || intento === null) {
        throw errorDelBorde(
          "El servidor contestó el catálogo de plantillas en una forma que no se entiende.",
          502,
        )
      }

      /*
       * ⚠️ La clave del índice lleva un separador que NO puede aparecer en un
       * nombre de plantilla de Meta ni en un código de idioma (los dos son
       * `[a-z0-9_]`), por el mismo motivo por el que `pantalla.tsx` serializa con
       * `JSON.stringify` en vez de `join`: dos ternas distintas que colapsen en la
       * misma clave cruzarían la fila con la plantilla equivocada.
       */
      const porTerna = new Map<string, PlantillaDelBrazo>()
      for (const plantilla of catalogo.templates) {
        porTerna.set(`${plantilla.name}\u0000${plantilla.language}`, plantilla)
      }

      const filas: FilaParaEnvios[] = filasCrudas.map((fila) => ({
        name: fila.name,
        language: fila.language,
        /*
         * ⛔⛔ CRUDO. Ver `FilaParaEnvios`: el veredicto lo decidió el servidor
         * sobre el mismo catálogo que usa el motor de envío, y acá sólo se copia.
         */
        sendability: fila.sendability,
        catalog: porTerna.get(`${fila.name}\u0000${fila.language}`) ?? null,
      }))

      /*
       * ⚠️ `lastSyncedAt` sale del CATÁLOGO y no de la primera fila. Las filas lo
       * traen repetido —es un hecho del brazo, no de la fila— y una lista VACÍA no
       * tiene ninguna fila de donde sacarlo, que es justo el brazo del que más
       * importa saber si sincronizó (`CA-61`).
       */
      return {
        filas,
        lastSyncedAt: catalogo.lastSyncedAt,
        lastAttempt: intento,
        catalogoDelBrazo: catalogo.templates,
      }
    },

    async agregar(brazo: Arm, name: string, language: string): Promise<void> {
      const ruta: SendListPath = rutaDeLaLista(brazo)

      await pedir(ruta, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ name, language }),
      })
    },

    async quitar(brazo: Arm, name: string, language: string): Promise<void> {
      const ruta: SendListTargetPath = rutaDeUnaFila(brazo, name, language)

      await pedir(ruta, {
        method: "DELETE",
        headers: { Accept: "application/json" },
      })
    },
  }
}
